import { useState } from 'react'

import EyeOffIcon from '@heroicons/react/outline/EyeOffIcon'
import LocationMarkerIcon from '@heroicons/react/outline/LocationMarkerIcon'
import PencilAltIcon from '@heroicons/react/outline/PencilAltIcon'
import ShieldExclamationIcon from '@heroicons/react/outline/ShieldExclamationIcon'
import TrashIcon from '@heroicons/react/outline/TrashIcon'
import { useDecision } from '@optimizely/react-sdk'
import { useHistory, useLocation } from 'react-router'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { PinnedInto, Post } from '@tribeplatform/gql-client/types'
import {
  useAuthToken,
  useDeletePost,
  useHidePost,
  usePinPostToSpace,
  useSpaceUtil,
  useUnhidePost,
  useUnpinPostFromSpace,
} from '@tribeplatform/react-sdk/hooks'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'

import { FeaturesList } from '../../lib/optimizely/feature.list'
import { AuditLogsModal } from '../AuditLog/AuditLogsModal'
import { PostContext } from './@types'
import { PostModal } from './PostModal'

export const PostOptions = ({
  post,
  context,
}: {
  post: Post
  context: PostContext
}) => {
  const { generatePath, generatePostPath } = useSpaceUtil()
  const history = useHistory()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [auditLogsModal, setAuditLogsModal] = useState<boolean>(false)
  const { mutate: deletePost } = useDeletePost()
  const { mutate: hidePost } = useHidePost()
  const { mutate: unhidePost } = useUnhidePost()
  const { mutate: pinPostToSpace } = usePinPostToSpace()
  const { mutate: unpinPostFromSpace } = useUnpinPostFromSpace()

  const [canUpdatePost, canRemovePost, canHidePost] = hasScopesPermission(
    post,
    ['updatePost', 'removePost', 'hidePost'],
  )

  const {
    data: { network },
  } = useAuthToken()
  const [canUpdateNetwork] = hasScopesPermission(network, ['updateNetwork'])

  const [canPinPost, canUnpinPost] = hasScopesPermission(post?.space, [
    'pinPostToSpace',
    'unpinPostFromSpace',
  ])

  const [decisionAuditLogs] = useDecision(FeaturesList.AuditLogs)

  const isPinned = post?.pinnedInto?.includes(PinnedInto.SPACE)
  const canPin = canPinPost && (context === 'post' || context === 'space')
  const canUnpin = canUnpinPost && (context === 'post' || context === 'space')

  const openModal = () => setOpen(true)
  const removePost = () => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      deletePost(
        {
          id: post.id,
        },
        {
          onSuccess: () => {
            const postAddress = generatePostPath(post?.space, post)
            if (location.pathname === postAddress) {
              const space = post?.space
              history.replace(space ? generatePath(space) : '/')
            }
          },
        },
      )
    }
  }

  const toggleHidePost = () => {
    if (post.isHidden) {
      unhidePost({
        id: post.id,
      })
    } else {
      hidePost({
        id: post.id,
      })
    }
  }
  if (!canHidePost && !canRemovePost && !canUpdatePost) return null

  return (
    <>
      <Dropdown placement="bottom-end">
        <Dropdown.ButtonMinimal className="p-2" />
        <Dropdown.Items className="w-56 divide-y divide-surface-200/50">
          <div className="pb-1">
            {canPin && isPinned && (
              <Dropdown.Item
                leadingIcon={<LocationMarkerIcon />}
                onClick={() => {
                  unpinPostFromSpace({ postId: post.id })
                }}
              >
                Unpin
              </Dropdown.Item>
            )}
            {canUnpin && !isPinned && (
              <Dropdown.Item
                leadingIcon={<LocationMarkerIcon />}
                onClick={() => {
                  pinPostToSpace({ postId: post.id })
                }}
              >
                Pin
              </Dropdown.Item>
            )}
            {canUpdatePost && (
              <Dropdown.Item
                leadingIcon={<PencilAltIcon />}
                onClick={openModal}
              >
                Edit
              </Dropdown.Item>
            )}
            {canRemovePost && (
              <Dropdown.Item onClick={removePost} leadingIcon={<TrashIcon />}>
                Delete
              </Dropdown.Item>
            )}
          </div>
          {canHidePost || (decisionAuditLogs?.enabled && canUpdateNetwork) ? (
            <div className="pt-1">
              {decisionAuditLogs?.enabled && canUpdateNetwork && (
                <Dropdown.Item
                  leadingIcon={<ShieldExclamationIcon />}
                  onClick={() => setAuditLogsModal(true)}
                >
                  Audit logs
                </Dropdown.Item>
              )}
              <Dropdown.Item
                leadingIcon={<EyeOffIcon />}
                onClick={toggleHidePost}
              >
                {post.isHidden ? 'Unhide' : 'Hide'}
              </Dropdown.Item>
            </div>
          ) : null}
        </Dropdown.Items>
      </Dropdown>
      {auditLogsModal && (
        <AuditLogsModal
          postId={post?.id}
          onClose={() => setAuditLogsModal(false)}
          open
        />
      )}
      {open && <PostModal post={post} onClose={() => setOpen(false)} />}
    </>
  )
}
